import { Truck, Clock, MapPin, Package, ShieldCheck } from "lucide-react";

const ZONES = [
  {
    icon: Clock,
    zone: "Within Ahmedabad city",
    urgent: "24 hours (urgent delivery)",
    normal: "2 to 3 days (standard delivery)",
    note: "Urgent slots are subject to courier availability. Please contact support before placing an urgent order.",
  },
  {
    icon: MapPin,
    zone: "Within Gujarat (outside Ahmedabad)",
    urgent: "Not applicable",
    normal: "2 to 4 business days",
    note: "Delivery may take longer for pincodes served by state transport only.",
  },
  {
    icon: Package,
    zone: "Outside Gujarat (rest of India)",
    urgent: "Not applicable",
    normal: "Within 7 business days",
    note: "Remote pincodes and North-East regions may take 1 to 2 additional days.",
  },
];

const NOTES = [
  "Delivery timelines start once the order is packed and handed to our courier partner. This usually happens within 24 hours of order confirmation on business days.",
  "Orders placed on Sundays and public holidays are processed on the next business day.",
  "Shipping charges are extra and are calculated based on the delivery city at checkout. Shipping charges are non-refundable unless the item received is damaged, defective or incorrect.",
  "Once shipped, the tracking number is shared over WhatsApp and email. You can also check the status inside the My Orders section.",
  "In case of address issues or wrong pincode, the courier partner will attempt delivery up to two times before returning the order to us. Please share correct details at checkout.",
];

export default function DeliveryPolicy() {
  return (
    <div className="mx-auto max-w-3xl px-4 py-14" data-testid="delivery-policy-page">
      <div className="text-xs uppercase tracking-[0.3em] text-[#F48E2E] font-bold mb-2">Shipping</div>
      <h1 className="font-display text-4xl md:text-5xl mb-3">Delivery Policy</h1>
      <p className="text-gray-700 leading-relaxed mb-10">
        We work with reliable courier partners to deliver your order as quickly as possible. Delivery timelines depend on the destination and product availability with the exhibitor.
      </p>

      <div className="space-y-4">
        {ZONES.map((z, i) => (
          <section key={i} data-testid={`delivery-zone-${i}`} className="p-6 bg-white rounded-2xl border border-gray-200">
            <div className="flex items-start gap-4">
              <div className="h-11 w-11 rounded-full bg-[#FEF3E6] flex items-center justify-center flex-shrink-0">
                <z.icon className="h-5 w-5 text-[#F48E2E]" />
              </div>
              <div className="flex-1">
                <h2 className="font-display text-xl mb-3">{z.zone}</h2>
                <div className="grid sm:grid-cols-2 gap-3 mb-3">
                  <div className="p-3 rounded-xl bg-gray-50 border border-gray-100">
                    <div className="text-[10px] uppercase tracking-widest text-gray-500 mb-1">Urgent</div>
                    <div className="text-sm font-medium">{z.urgent}</div>
                  </div>
                  <div className="p-3 rounded-xl bg-gray-50 border border-gray-100">
                    <div className="text-[10px] uppercase tracking-widest text-gray-500 mb-1">Standard</div>
                    <div className="text-sm font-medium">{z.normal}</div>
                  </div>
                </div>
                <p className="text-xs text-gray-600">{z.note}</p>
              </div>
            </div>
          </section>
        ))}

        <section data-testid="delivery-notes" className="p-6 bg-white rounded-2xl border border-gray-200">
          <div className="flex items-center gap-3 mb-4">
            <ShieldCheck className="h-5 w-5 text-[#F48E2E]" />
            <h2 className="font-display text-xl">Important notes</h2>
          </div>
          <ul className="space-y-3">
            {NOTES.map((n, i) => (
              <li key={i} className="flex gap-3">
                <span className="mt-2 h-1.5 w-1.5 rounded-full bg-[#F48E2E] flex-shrink-0" />
                <span className="text-gray-700 leading-relaxed">{n}</span>
              </li>
            ))}
          </ul>
        </section>

        <section className="p-6 rounded-2xl bg-gradient-to-br from-[#F48E2E] to-[#111827] text-white">
          <div className="flex items-center gap-3 mb-3">
            <Truck className="h-5 w-5" />
            <h2 className="font-display text-xl">Need urgent delivery in Ahmedabad?</h2>
          </div>
          <p className="text-white/95 mb-4">
            For same-day delivery within Ahmedabad city, please contact our support team on WhatsApp before placing your order so we can confirm availability with the exhibitor.
          </p>
          <a href="https://wa.me/917069225050" target="_blank" rel="noreferrer" className="inline-block px-5 py-2.5 rounded-full bg-white text-[#111827] font-medium text-sm hover:bg-gray-100">
            WhatsApp Support
          </a>
        </section>
      </div>
    </div>
  );
}
